/* eslint-disable */
// src/forms/localMachine/editLocalMachineForm.js

import React, { Component } from "react";
import PropTypes from "prop-types";
import { Button, ButtonGroup, TextField, Grid, Container, Paper, Typography, Box, MenuItem } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";
import LocalMachineService from "services/localMachineService";
import FormulaService from "services/formulaService";
import VisitTransactionService from "services/visitTransactionService";
import { hasRent, getRentPrice, getRentFrequency } from "utils/rent";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import {
  dateOnlyFromDateTime,
  getDefaultOperationDate,
  isBeforeMinOperationDate,
  isFutureOperationDate,
  isValidOperationDate,
  parseDateOnlyToLocalDate,
  toLocalDateOnlyString,
} from "utils/operationDate";
import { segmentedButtonSx } from "components/common/buttonStyles";
import { findCanonicalFormula, formatFormulaLabel, getFormulaId } from "utils/formula";
import AppNumberInput from "components/common/AppNumberInput";
import AppFormContext from "components/common/AppFormContext";

class EditLocalMachineForm extends Component {
  constructor(props) {
    super(props);
    const lm = props.localMachine || {};
    const type = lm?.MachineType?.Type || "";
    const rented = hasRent ? hasRent(lm) : !!lm?.RentId;
    const currentFormula = lm?.Formula || {};

    const initialFormula = {
      Type: currentFormula.Type || "",
      Value: currentFormula.Value || "",
    };

    const initialIsAccumulative = !!lm?.IsAccumulative;

    const initialRent = {
      Price: getRentPrice ? (getRentPrice(lm) ?? "") : (lm.RentPrice ?? ""),
      PaymentFrequency: getRentFrequency ? (getRentFrequency(lm) ?? "") : (lm.RentPaymentFrequency ?? ""),
    };

    this.state = {
      machineTypeName: type,
      isRented: rented,
      localMachineId: lm?.Id,
      form: { IsAccumulative: initialIsAccumulative },
      formulaForm: initialFormula,
      formulas: [],
      formulaMode: currentFormula.Type ? "existing" : "none",
      selectedFormulaId: currentFormula.Id ?? "",
      originalFormulaForm: initialFormula,
      originalIsAccumulative: initialIsAccumulative,
      rentForm: initialRent,
      originalRentForm: initialRent,
      effectiveDate: getDefaultOperationDate(),
      lastLocalCollectionDate: null,
      generalError: "",
      showRequiredErrors: false,
    };
  }

  async componentDidMount() {
    try {
      const formulas = await FormulaService.getFormulas();
      const canonicalFormula = findCanonicalFormula(
        formulas,
        this.props.localMachine?.Formula || this.props.localMachine
      );
      this.setState({
        formulas,
        formulaMode: canonicalFormula
          ? "existing"
          : this.props.localMachine?.Formula?.Type
            ? "custom"
            : "none",
        selectedFormulaId: getFormulaId(canonicalFormula) ?? "",
      });

      const localId = Number(this.props.local?.Id);
      if (!Number.isInteger(localId) || localId <= 0) return;

      const lastCollection = await VisitTransactionService.getLastCollectionByLocal(localId);
      const lastCollectionDate = parseDateOnlyToLocalDate(
        dateOnlyFromDateTime(lastCollection?.visit?.DateTime)
      );

      if (!lastCollectionDate) return;

      this.setState({
        lastLocalCollectionDate: lastCollectionDate,
      });
    } catch (error) {
      console.error("Error obteniendo la última recaudación del local:", error);
    }
  }

  handleDateChange = (date) => {
    this.setState({ effectiveDate: date, generalError: "" });
  };

  getMinEffectiveDate = ({ canEditRent = false } = {}) => {
    const localMachineStartDate = parseDateOnlyToLocalDate(
      dateOnlyFromDateTime(this.props.localMachine?.StartDateTime)
    );
    const lastLocalCollectionDate = this.state.lastLocalCollectionDate;

    if (!localMachineStartDate) return lastLocalCollectionDate;
    if (!lastLocalCollectionDate) return localMachineStartDate;

    return localMachineStartDate.getTime() >= lastLocalCollectionDate.getTime()
      ? localMachineStartDate
      : lastLocalCollectionDate;
  };

  getEffectiveDateErrorMessage = ({ canEditRent = false } = {}) => {
    const { effectiveDate } = this.state;
    const minDate = this.getMinEffectiveDate({ canEditRent });
    const lastLocalCollectionDate = this.state.lastLocalCollectionDate;

    if (!isValidOperationDate(effectiveDate)) {
      return "Selecciona una fecha válida.";
    }
    if (isFutureOperationDate(effectiveDate)) {
      return "No se puede seleccionar una fecha futura.";
    }
    if (minDate && isBeforeMinOperationDate(effectiveDate, minDate)) {
      if (
        lastLocalCollectionDate &&
        minDate.getTime() === lastLocalCollectionDate.getTime()
      ) {
        return canEditRent
          ? "La fecha de edición del alquiler no puede ser anterior a la última recaudación del local."
          : "La fecha de edición debe ser posterior a la última recaudación del local.";
      }
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina.";
    }
    return "";
  };

  getApiErrorMessage = (error, fallback) => {
    const status = error?.response?.status;
    const apiError = error?.response?.data?.error || fallback;
    const apiCode = error?.response?.data?.code || "";

    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_MACHINE_CREATION") {
      return "La fecha seleccionada no puede ser anterior a la fecha de creación de la máquina.";
    }
    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_CURRENT_LOCAL_MACHINE_START") {
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina.";
    }
    if (status === 400 && apiCode === "EFFECTIVE_DATE_CANNOT_BE_FUTURE") {
      return "No se puede seleccionar una fecha futura.";
    }

    return apiError || fallback;
  };

    findFormulaOption = (type, value) =>
    (this.state.formulas || []).find(
      (formula) =>
        String(formula?.Type || "") === String(type || "") &&
        Number(formula?.Value) === Number(value)
    ) || null;

  getSelectedFormulaPreset = () => {
    const type = String(this.state.formulaForm?.Type || "").trim();
    const value = Number(this.state.formulaForm?.Value);

    if (this.state.formulaMode === "none") return "no-formula";
    if (type === "/" && value === 10) return "/10";
    if (type === "*" && value === 10) return "*10";
    return "custom";
  };

  applyFormulaSelection = ({ Type, Value }) => {
    const type = String(Type || "").trim();
    const rawValue = Value == null ? "" : String(Value);
    const existing = type && rawValue !== "" ? this.findFormulaOption(type, rawValue) : null;

    this.setState({
      formulaMode: existing ? "existing" : "custom",
      selectedFormulaId: existing ? getFormulaId(existing) : "",
      formulaForm: { Type: type, Value: rawValue },
      generalError: "",
    });
  };

  handlePresetFormulaTragamoneda = (type) => {
    this.applyFormulaSelection({ Type: type, Value: "10" });
  };

  handlePresetNoFormula = () => {
    this.setState({
      formulaMode: "none",
      selectedFormulaId: "",
      formulaForm: { Type: "", Value: "" },
      generalError: "",
    });
  };

  handleToggleFormulaAdjust = () => {
    const selectedPreset = this.getSelectedFormulaPreset();

    this.setState((prev) => ({
      formulaMode: "custom",
      selectedFormulaId: "",
      formulaForm:
        selectedPreset === "custom"
          ? prev.formulaForm
          : { Type: "", Value: "" },
      generalError: "",
    }));
  };

  handleCustomFormulaChange = (field, value) => {
    this.setState((prev) => ({
      formulaMode: "custom",
      selectedFormulaId: "",
      formulaForm: { ...prev.formulaForm, [field]: value },
      generalError: "",
    }));
  };

  hasRentChanges = () => {
    const { rentForm, originalRentForm } = this.state;

    const normPrice = (v) => (v == null || String(v).trim() === "" ? "" : String(Number(v)));
    const normFreq = (v) => (v == null ? "" : String(v).trim().toLowerCase());

    return (
      normPrice(rentForm.Price) !== normPrice(originalRentForm.Price) ||
      normFreq(rentForm.PaymentFrequency) !== normFreq(originalRentForm.PaymentFrequency)
    );
  };

  getRentPriceErrorMessage = () => {
    const rawPrice = this.state.rentForm.Price;

    if (
      rawPrice === "" ||
      rawPrice === null ||
      rawPrice === undefined
    ) {
      return "El precio del alquiler debe ser mayor que cero.";
    }

    const price = Number(rawPrice);

    if (!Number.isFinite(price) || price <= 0) {
      return "El precio del alquiler debe ser mayor que cero.";
    }

    return "";
  };

  hasFormulaChanges = () => {
    const { formulaForm, originalFormulaForm, form, originalIsAccumulative, machineTypeName } = this.state;

    const normType = (v) => (v == null ? "" : String(v).trim().toLowerCase());
    const normValue = (v) =>
      v == null || String(v).trim() === ""
        ? ""
        : String(Number(v));

    const formulaChanged =
      normType(formulaForm.Type) !== normType(originalFormulaForm.Type) ||
      normValue(formulaForm.Value) !== normValue(originalFormulaForm.Value);

    const accumChanged =
      machineTypeName === "Tragamoneda"
        ? Boolean(form.IsAccumulative) !== Boolean(originalIsAccumulative)
        : false;

    return formulaChanged || accumChanged;
  };

  render() {
    const { closeForm, onUpdateSuccess } = this.props;
    const { machineTypeName, isRented, form, rentForm, effectiveDate, generalError } = this.state;

    const isRockola = machineTypeName === "Rockola";
    const canEditFormula = !isRented && !isRockola;
    const canEditRent = isRented;

    const formulaChanged = canEditFormula ? this.hasFormulaChanges() : false;
    const canSaveFormula = canEditFormula && formulaChanged;

    const rentChanged = canEditRent ? this.hasRentChanges() : false;
    const rentPriceError = canEditRent ? this.getRentPriceErrorMessage() : "";
    const canSaveRent = canEditRent && rentChanged && !rentPriceError;
    const sectionTitleSx = { mb: 1 };
    const minEffectiveDate = this.getMinEffectiveDate({ canEditRent });
    const effectiveDateError = this.getEffectiveDateErrorMessage({ canEditRent });
    const today = getDefaultOperationDate();
    const currentMachine = this.props.localMachine || {};
    const currentRentPrice = getRentPrice(currentMachine);
    const currentRentFrequency = getRentFrequency(currentMachine);
    const contextLines = [
      this.props.local?.Name ? `Local: ${this.props.local.Name}` : null,
      canEditRent
        ? `Alquiler actual: $ ${currentRentPrice ?? "—"}${currentRentFrequency ? ` · ${currentRentFrequency}` : ""}`
        : canEditFormula
          ? `Fórmula actual: ${formatFormulaLabel(currentMachine, { fallback: "Sin fórmula" })}`
          : null,
      canEditFormula && machineTypeName === "Tragamoneda"
        ? `Modo actual: ${currentMachine.IsAccumulative ? "Acumulativa" : "Con borrado"}`
        : null,
    ].filter(Boolean);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (canEditRent && !canSaveRent) {
        if (rentChanged && rentPriceError) {
          this.setState({ showRequiredErrors: true, generalError: "" });
        }
        return;
      }
      if (canEditFormula && !canSaveFormula) return;

      if (effectiveDateError) {
        return;
      }

      try {
        const isTraga = this.state.machineTypeName === "Tragamoneda";
        const isFormulaType =
          ["Tragamoneda", "Pool", "Tejo", "Peluchera"].includes(this.state.machineTypeName);

        if (canEditFormula) {
          let cleanFormulaId = null;

          if (isFormulaType) {
            const { Type, Value } = this.state.formulaForm;
            const hasType = Type && String(Type).trim() !== "";
            const hasValue = Value !== null && Value !== undefined && String(Value).trim() !== "";
            const hasFormulaData = hasType || hasValue;

            if (this.state.formulaMode === "custom" && (!hasType || !hasValue)) {
              this.setState({ showRequiredErrors: true, generalError: "" });
              return;
            }

            if (hasFormulaData) {
              if (this.state.formulaMode === "existing") {
                cleanFormulaId = Number(this.state.selectedFormulaId);
              }
            }
          }

          const payload = {
            FormulaId: cleanFormulaId,
            Formula:
              cleanFormulaId == null && isFormulaType && this.state.formulaForm.Type
                ? {
                    Type: this.state.formulaForm.Type,
                    Value: this.state.formulaForm.Value,
                  }
                : null,
            IsAccumulative: isTraga ? !!form.IsAccumulative : false,
            EffectiveDate: toLocalDateOnlyString(effectiveDate),
          };

          const updated = await LocalMachineService.updateLocalMachine(
            this.state.localMachineId,
            payload
          );
          if (updated) {
            onUpdateSuccess?.();
          }
          return;
        }

        if (canEditRent) {
          const rentPriceError = this.getRentPriceErrorMessage();

          if (rentPriceError) {
            this.setState({ showRequiredErrors: true, generalError: "" });
            return;
          }

          const price = Number(rentForm.Price);
          const paymentFrequency = rentForm.PaymentFrequency ?? null;

          const updated = await LocalMachineService.updateLocalMachineRent(
            this.state.localMachineId,
            {
              Price: price,
              PaymentFrequency: paymentFrequency,
              EffectiveDate: toLocalDateOnlyString(effectiveDate),
            }
          );

          if (updated) {
            onUpdateSuccess?.();
          }
          return;
        }

        return closeForm?.();
      } catch (error) {
        console.error("Error actualizando LocalMachine:", error);
        this.setState({
          generalError: this.getApiErrorMessage(
            error,
            "No se pudo actualizar la configuración de la máquina."
          ),
        });
      }
    };

    return (
      <Container component={Paper} style={{ padding: "20px", marginTop: "10px", width: "100%" }}>
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AppFormContext
                title={[machineTypeName, currentMachine?.Machine?.Name || currentMachine?.Name || "Sin nombre"].filter(Boolean).join(" ")}
                lines={contextLines}
              />
            </Grid>
            {!canEditFormula && !canEditRent && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Esta máquina {isRented ? "está alquilada" : "es Rockola"} y no admite edición de configuración.
                </Typography>
              </Grid>
            )}
            {canEditFormula &&
              ["Tragamoneda", "Pool", "Tejo", "Peluchera"].includes(machineTypeName) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={sectionTitleSx}>
                    Fórmula
                  </Typography>

                  <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                    <ButtonGroup variant="outlined" size="small" sx={segmentedButtonSx}>
                      <Button
                        type="button"
                        variant={this.getSelectedFormulaPreset() === "/10" ? "contained" : "outlined"}
                        onClick={() => this.handlePresetFormulaTragamoneda("/")}
                      >
                        / 10
                      </Button>
                      <Button
                        type="button"
                        variant={this.getSelectedFormulaPreset() === "*10" ? "contained" : "outlined"}
                        onClick={() => this.handlePresetFormulaTragamoneda("*")}
                      >
                        * 10
                      </Button>
                      {machineTypeName === "Tragamoneda" && (
                        <Button
                          type="button"
                          variant={
                            this.getSelectedFormulaPreset() === "no-formula"
                              ? "contained"
                              : "outlined"
                          }
                          onClick={this.handlePresetNoFormula}
                        >
                          Sin fórmula
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant={this.getSelectedFormulaPreset() === "custom" ? "contained" : "outlined"}
                        onClick={this.handleToggleFormulaAdjust}
                      >
                        Ajustar
                      </Button>
                    </ButtonGroup>
                  </Box>

                  {this.getSelectedFormulaPreset() === "custom" && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Tipo de fórmula"
                          required
                          select
                          value={this.state.formulaForm.Type}
                          onChange={(e) =>
                            this.handleCustomFormulaChange("Type", e.target.value)
                          }
                          error={this.state.showRequiredErrors && !this.state.formulaForm.Type}
                          helperText={this.state.showRequiredErrors && !this.state.formulaForm.Type ? "Seleccioná un tipo de fórmula." : ""}
                        >
                          {["+", "-", "*", "/"].map((operator) => (
                            <MenuItem key={operator} value={operator}>
                              {operator}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <AppNumberInput
                          fullWidth
                          label="Valor"
                          required
                          decimalScale={0}
                          allowNegative={false}
                          min={1}
                          value={this.state.formulaForm.Value}
                          onValueChange={(value) =>
                            this.handleCustomFormulaChange("Value", value)
                          }
                          error={this.state.showRequiredErrors && !this.state.formulaForm.Value}
                          helperText={this.state.showRequiredErrors && !this.state.formulaForm.Value ? "Ingresá un valor." : ""}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}

            {canEditFormula && machineTypeName === "Tragamoneda" && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={sectionTitleSx}>
                  Modo de conteo
                </Typography>
                <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                  <ButtonGroup variant="outlined" size="small" sx={segmentedButtonSx}>
                    <Button
                      type="button"
                      aria-pressed={!!form.IsAccumulative}
                      variant={form.IsAccumulative ? "contained" : "outlined"}
                      onClick={() =>
                        this.setState((prev) => ({
                          ...prev,
                          form: { ...prev.form, IsAccumulative: true },
                        }))
                      }
                    >
                      Acumulativa
                    </Button>
                    <Button
                      type="button"
                      aria-pressed={!form.IsAccumulative}
                      variant={!form.IsAccumulative ? "contained" : "outlined"}
                      onClick={() =>
                        this.setState((prev) => ({
                          ...prev,
                          form: { ...prev.form, IsAccumulative: false },
                        }))
                      }
                    >
                      Con borrado
                    </Button>
                  </ButtonGroup>
                </Box>
              </Grid>
            )}

            {canEditRent && (
              <>
                <Grid item xs={12}>
                  <AppNumberInput
                    fullWidth
                    label="Precio del alquiler"
                    required
                    name="Price"
                    value={rentForm.Price}
                    onValueChange={(value) =>
                      this.setState((prev) => ({
                        ...prev,
                        rentForm: { ...prev.rentForm, Price: value },
                      }))
                    }
                    decimalScale={2}
                    fixedDecimalScale={false}
                    allowNegative={false}
                    min={0}
                    error={this.state.showRequiredErrors && Boolean(rentPriceError)}
                    helperText={this.state.showRequiredErrors ? rentPriceError : ""}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Frecuencia de pago"
                    name="PaymentFrequency"
                    value={rentForm.PaymentFrequency}
                    onChange={(e) =>
                      this.setState((prev) => ({
                        ...prev,
                        rentForm: { ...prev.rentForm, PaymentFrequency: e.target.value },
                      }))
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
                <DatePicker
                  label="Fecha de modificación"
                  value={effectiveDate}
                  onChange={this.handleDateChange}
                  minDate={minEffectiveDate || undefined}
                  maxDate={today}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(effectiveDateError),
                      helperText: effectiveDateError,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {generalError && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {generalError}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CancelButton onClick={closeForm} fullWidth>
                    Cancelar
                  </CancelButton>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AcceptButton
                    type="submit"
                    disabled={
                      canEditRent
                        ? !rentChanged || Boolean(effectiveDateError)
                        : canEditFormula
                          ? !canSaveFormula || Boolean(effectiveDateError)
                          : true
                    }
                    fullWidth
                  >
                    Guardar cambios
                  </AcceptButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Container>
    );
  }
}

EditLocalMachineForm.propTypes = {
  local: PropTypes.object.isRequired,
  localMachine: PropTypes.object.isRequired,
  closeForm: PropTypes.func.isRequired,
  onUpdateSuccess: PropTypes.func.isRequired,
};

export default EditLocalMachineForm;
